/**
 * Opt-in rewarded ads (feature 'ads'). STRICT PRODUCT RULE: an ad is shown only when the player presses an opt-in
 * button for a visible bonus; the game never shows one by itself and never needs one to progress. The placements and
 * their daily caps: ../monetization-catalog.ts (AD_PLACEMENTS).
 *
 * Flow:
 *  1. ads.status: the placements, the daily caps and what the player has left today (the player's calendar day, the
 *     day of the game's own daily resets).
 *  2. ads.start { placement, provider, context }: under the player's profile lock the server checks that the bonus
 *     applies now (the won battle, the searched grave, the present, the tiredness...), that the caps allow it (per
 *     placement, AD_DAILY_CAP_TOTAL in all, config.ads.maxOpenTickets open tickets) and that this bonus was not taken
 *     (a context key per battle / grave / day), takes a SNAPSHOT of what the reward is for, and stores a ticket
 *     (C.adTickets; C.adPlayers keeps the player's recent tickets for the counts). The ticket's `ssv` goes to the ad
 *     SDK: userId = the player's obfuscated account id, customData = the ticketId.
 *  3. The provider verifies the view: AdMob calls the server-side verification URL (src/server/node/admob-ssv.ts
 *     checks Google's ECDSA signature, then verifyReward); 'dev-test' tickets are verified by ads.testReward. The
 *     transaction id is recorded once (C.adTransactions): a repeated callback changes nothing.
 *  4. grant: under the profile lock the reward of the snapshot is applied to the canonical game state by the game's
 *     own rules (a grave roll, a darts throw, the present) with the server's RNG, and the new state, the granted
 *     ticket (with its `result`), the player's ad record, the transaction id and the audit entry are ONE storage batch;
 *     the player's connections get profile.changed. The client reads the result with ads.ticket.
 * A ticket not verified within config.ads.ticketTtlMs expires (the sweep); expired and failed tickets do not count
 * against the caps. Rewards never depend on what the client says about the ad: only the provider's verification.
 */
import { sha256Hex } from '../../../shared/crypto/sha256';
import { type JsonObject, type JsonValue, cloneJson, isPlainObject } from '../../../shared/json';
import { BackendError } from '../../../shared/protocol/errors';
import { AD_PLACEMENT_LIST, type AdContext, type AdPlacement, type AdPlacementStatus, type AdProviderId, type AdTicketInfo, type AdsStatusResult, type PlayerId } from '../../../shared/protocol/model';
import { randomId } from '../../../shared/random';
import { RULE_OPS, SERVER_RULE_OPS, type StoredGameState, decodeState, encodeState, int32, isRuleError } from '../../../game/rules/index';
import {
  AD_DAILY_CAP_TOTAL,
  AD_PLACEMENTS,
  AD_REST_AMOUNT,
  AD_REST_MIN_TIRED,
  type AdPlacementDef,
  type AdPlacementId,
  BATTLE_DOUBLE_MAX_GOLD,
  BATTLE_DOUBLE_MODES,
  DARTS_FREE_BET,
} from '../monetization-catalog';
import { type ServerState, decideTimeZone, runRulesOp, serverRuleContext } from '../rules-runtime';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import { lastBattleOf, lastWinOf } from './battles';
import { LAST_PRESENT_KEY } from './commands';
import { C, type ProfileRecord, type ServiceDeps } from './deps';
import type { MonetizationAudit } from './monetization-audit';
import { PRESENT_KEY, type ProfileService, pendingPresent } from './profile';
import { withCurrency } from './store';

/** A stored ticket (C.adTickets, id = ticketId). */
export interface AdTicketRecord {
  ticketId: string;
  playerId: PlayerId;
  placement: AdPlacement;
  provider: AdProviderId;
  status: AdTicketInfo['status'];
  createdAt: number;
  expiresAt: number;
  /** The player's calendar day the ticket counts for. */
  day: number;
  /** The bonus it is for (one ticket per key: 'battle:<id>', 'grave:<id>:<n>', 'present:<day>'), or null. */
  contextKey: string | null;
  /** What the reward is computed from, taken at ads.start. */
  snapshot: JsonObject;
  ssvUserId: string;
  transactionId: string | null;
  grantedAt: number | null;
  result: JsonObject | null;
  failure: string | null;
}

/** The player's recent tickets (C.adPlayers, id = playerId): the daily counts and the used contexts. */
export interface AdPlayerRecord {
  recent: { ticketId: string; placement: AdPlacement; day: number; status: AdTicketInfo['status']; contextKey: string | null }[];
}

/** A verified view as the provider reports it (AdMob server-side verification callback). */
export interface VerifiedAdReward {
  provider: 'admob';
  transactionId: string;
  /** The ticketId (the ssv customData). */
  customData: string;
  /** The ssv userId. */
  userId: string;
  adUnit: string | null;
  rewardItem: string | null;
  rewardAmount: number | null;
}

const RECENT_KEPT = 200;
const TICKET_ID = /^at_[a-z0-9]{8,40}$/;
const PRUNE_EVERY_MS = 6 * 3600_000;

export interface AdsServiceOptions {
  /** The obfuscated account id of a player (StoreService.accountTag): the ssv userId. */
  userIdOf: (playerId: PlayerId) => string;
  placements?: Readonly<Record<AdPlacementId, AdPlacementDef>>;
}

export class AdsService {
  private readonly placements: Readonly<Record<AdPlacementId, AdPlacementDef>>;
  private readonly userIdOf: (playerId: PlayerId) => string;
  /** Open tickets: ticketId -> [playerId, expiresAt] (loaded once, then kept up to date). */
  private readonly open = new Map<string, [PlayerId, number]>();
  private openLoaded = false;
  private nextPruneAt = 0;
  private busy: Promise<void> | null = null;

  constructor(
    private readonly d: ServiceDeps,
    private readonly profiles: ProfileService,
    private readonly audit: MonetizationAudit,
    opts: AdsServiceOptions,
  ) {
    this.placements = opts.placements ?? AD_PLACEMENTS;
    this.userIdOf = opts.userIdOf;
  }

  providers(): AdProviderId[] {
    const out: AdProviderId[] = [];
    if (this.d.config.ads.admob) out.push('admob');
    if (this.d.config.ads.devTest) out.push('dev-test');
    return out;
  }

  get enabled(): boolean {
    return this.providers().length > 0;
  }

  // ---------------------------------------------------------------------------------------------- ads.status

  async status(playerId: PlayerId): Promise<AdsStatusResult> {
    const rec = await this.profiles.getRecord(playerId);
    const { day, resetsAt } = this.dayOf(rec);
    const mine = await this.playerRecord(playerId);
    const counting = mine.recent.filter((t) => t.day === day && (t.status === 'open' || t.status === 'granted'));
    const placements: AdPlacementStatus[] = AD_PLACEMENT_LIST.map((placement) => {
      const def = this.placements[placement];
      const usedToday = counting.filter((t) => t.placement === placement).length;
      return {
        placement,
        enabled: def.enabled,
        dailyCap: def.dailyCap,
        usedToday,
        remainingToday: def.enabled ? Math.max(0, def.dailyCap - usedToday) : 0,
        maxAmount: placement === 'ad_battle_double' ? BATTLE_DOUBLE_MAX_GOLD : null,
      };
    });
    return { providers: this.providers(), placements, dailyCapTotal: AD_DAILY_CAP_TOTAL, remainingToday: Math.max(0, AD_DAILY_CAP_TOTAL - counting.length), resetsAt };
  }

  // ---------------------------------------------------------------------------------------------- ads.start

  async start(playerId: PlayerId, placement: AdPlacement, provider: AdProviderId, context: AdContext = {}): Promise<AdTicketInfo> {
    if (!this.providers().includes(provider)) throw new BackendError('FORBIDDEN', 'This server does not verify ads of that provider', { reason: 'ad_provider_disabled', provider });
    const def = this.placements[placement];
    if (!def?.enabled) throw new BackendError('FORBIDDEN', 'This placement is switched off', { reason: 'placement_disabled', placement });
    return this.profiles.withRecord(playerId, async (rec) => {
      const now = this.d.clock.now();
      const { day, resetsAt } = this.dayOf(rec);
      const { contextKey, snapshot } = this.contextOf(rec, placement, context, day);
      const mine = await this.playerRecord(playerId);
      if (contextKey) {
        const used = mine.recent.find((t) => t.contextKey === contextKey && (t.status === 'open' || t.status === 'granted'));
        if (used) throw new BackendError('CONFLICT', 'This bonus was taken already', { reason: 'ad_context_used', ticketId: used.ticketId });
      }
      const counting = mine.recent.filter((t) => t.day === day && (t.status === 'open' || t.status === 'granted'));
      if (counting.filter((t) => t.placement === placement).length >= def.dailyCap) {
        throw new BackendError('INVALID_STATE', "Today's rewards of this placement are used up", { reason: 'ad_daily_cap', placement, dailyCap: def.dailyCap, resetsAt });
      }
      if (counting.length >= AD_DAILY_CAP_TOTAL) throw new BackendError('INVALID_STATE', "Today's ad rewards are used up", { reason: 'ad_daily_cap', placement, dailyCap: AD_DAILY_CAP_TOTAL, resetsAt });
      const openCount = mine.recent.filter((t) => t.status === 'open').length;
      if (openCount >= this.d.config.ads.maxOpenTickets) throw new BackendError('INVALID_STATE', 'Too many ads are waiting for their verification', { reason: 'too_many_ad_tickets', max: this.d.config.ads.maxOpenTickets });
      const t: AdTicketRecord = {
        ticketId: randomId(this.d.rng, 'at_', 12),
        playerId,
        placement,
        provider,
        status: 'open',
        createdAt: now,
        expiresAt: now + this.d.config.ads.ticketTtlMs,
        day,
        contextKey,
        snapshot,
        ssvUserId: this.userIdOf(playerId),
        transactionId: null,
        grantedAt: null,
        result: null,
        failure: null,
      };
      const next: AdPlayerRecord = { recent: [...mine.recent, { ticketId: t.ticketId, placement, day, status: 'open' as const, contextKey }].slice(-RECENT_KEPT) };
      const { entry, op } = this.audit.prepare({ area: 'ads', event: 'ticket', playerId, provider, ok: true, details: { ticketId: t.ticketId, placement, ...(contextKey ? { context: contextKey } : {}) } });
      await this.d.storage.batch([putOp(C.adTickets, t.ticketId, t), putOp(C.adPlayers, playerId, next), op]);
      this.audit.stored(entry);
      this.open.set(t.ticketId, [playerId, t.expiresAt]);
      return this.info(t);
    });
  }

  /** The bonus a placement would reward now, as a context key and the snapshot of its inputs (INVALID_STATE otherwise). */
  private contextOf(rec: ProfileRecord, placement: AdPlacement, context: AdContext, day: number): { contextKey: string | null; snapshot: JsonObject } {
    const invalid = (problem: string) => new BackendError('INVALID_STATE', 'There is nothing to reward here now', { reason: 'ad_context_invalid', placement, problem });
    const s = rec.serverState ?? {};
    const gameState = rec.profile.gameState;
    if (!gameState) throw invalid('no_profile');
    switch (placement) {
      case 'ad_battle_double': {
        const win = lastWinOf(s);
        if (!win || win.battleId !== context.battleId) throw invalid('no_won_battle');
        if (!BATTLE_DOUBLE_MODES.includes(win.mode)) throw invalid('mode_not_eligible');
        const gold = Math.min(BATTLE_DOUBLE_MAX_GOLD, Math.max(0, Math.floor(win.gold)));
        if (gold <= 0) throw invalid('no_gold');
        return { contextKey: `battle:${win.battleId}`, snapshot: { battleId: win.battleId, gold } };
      }
      case 'ad_grave_extra': {
        const last = lastBattleOf(s);
        if (!last || last.battleId !== context.battleId) throw invalid('no_won_battle');
        const index = context.index ?? 0;
        const grave = last.graves[index - 1];
        if (!grave) throw invalid('no_grave');
        if (!grave.searched) throw invalid('grave_not_searched');
        return { contextKey: `grave:${last.battleId}:${index}`, snapshot: { battleId: last.battleId, index, mode: last.mode, stage: last.stage, name: grave.name, cpu_level: grave.cpu_level } };
      }
      case 'ad_darts_free':
        return { contextKey: null, snapshot: { currency: DARTS_FREE_BET.currency, amount: DARTS_FREE_BET.amount } };
      case 'ad_daily_gift_x2': {
        const pending = pendingPresent(s);
        const last = isPlainObject(s[LAST_PRESENT_KEY]) ? (s[LAST_PRESENT_KEY] as JsonObject) : null;
        const presentDay = pending ? pending.day : last && typeof last.day === 'number' ? last.day : null;
        if (presentDay === null || presentDay !== day) throw invalid('no_present');
        return { contextKey: `present:${presentDay}`, snapshot: { day: presentDay } };
      }
      case 'ad_rest': {
        const tired = Number(decodeState(gameState).player.tired) || 0;
        if (tired < AD_REST_MIN_TIRED) throw invalid('not_tired');
        return { contextKey: null, snapshot: {} };
      }
    }
    throw invalid('unknown_placement');
  }

  // ---------------------------------------------------------------------------------------------- ads.ticket

  async ticket(playerId: PlayerId, ticketId: string): Promise<AdTicketInfo> {
    const t = await this.d.storage.get<AdTicketRecord>(C.adTickets, ticketId);
    if (!t || t.playerId !== playerId) throw new BackendError('NOT_FOUND', 'No such ad ticket', { reason: 'ad_ticket_not_found', ticketId });
    return this.info(t);
  }

  // ---------------------------------------------------------------------------------------------- verification

  /** ads.testReward: the development provider verifies a 'dev-test' ticket of the asking player. */
  async testReward(playerId: PlayerId, ticketId: string): Promise<AdTicketInfo> {
    if (!this.d.config.ads.devTest) throw new BackendError('FORBIDDEN', 'The development ad provider is off on this server', { reason: 'ad_provider_disabled', provider: 'dev-test' });
    const t = await this.d.storage.get<AdTicketRecord>(C.adTickets, ticketId);
    if (!t || t.playerId !== playerId) throw new BackendError('NOT_FOUND', 'No such ad ticket', { reason: 'ad_ticket_not_found', ticketId });
    if (t.provider !== 'dev-test') throw new BackendError('FORBIDDEN', 'Only a dev-test ticket is verified here', { reason: 'ad_provider_disabled', provider: t.provider });
    if (t.status !== 'open') throw new BackendError('INVALID_STATE', 'The ticket is not waiting any more', { reason: 'ad_ticket_not_open', status: t.status });
    return this.info(await this.grant(ticketId, { provider: 'dev-test', transactionId: `devtest.${ticketId}` }));
  }

  /**
   * A view the provider verified (the signature was checked by the caller). Idempotent by the transaction id.
   * Returns what happened: 'granted', 'duplicate', 'unknown_ticket', 'user_mismatch', 'provider_mismatch', 'not_open',
   * 'failed'.
   */
  async verifyReward(v: VerifiedAdReward): Promise<string> {
    const txId = transactionDocId(v.provider, v.transactionId);
    if (await this.d.storage.get(C.adTransactions, txId)) return 'duplicate';
    const details: JsonObject = { transactionId: v.transactionId.slice(0, 128), adUnit: v.adUnit, rewardItem: v.rewardItem, rewardAmount: v.rewardAmount };
    const ticket = TICKET_ID.test(v.customData) ? await this.d.storage.get<AdTicketRecord>(C.adTickets, v.customData) : null;
    const refuse = async (outcome: string) => {
      const { entry, op } = this.audit.prepare({ area: 'ads', event: 'verify', playerId: ticket?.playerId ?? null, provider: v.provider, ok: false, details: { ...details, ticketId: ticket?.ticketId ?? null, outcome } });
      await this.d.storage.batch([putOp(C.adTransactions, txId, { ticketId: ticket?.ticketId ?? null, at: this.d.clock.now() }), op]);
      this.audit.stored(entry);
      return outcome;
    };
    if (!ticket) return refuse('unknown_ticket');
    if (ticket.ssvUserId !== v.userId) return refuse('user_mismatch');
    if (ticket.provider !== v.provider) return refuse('provider_mismatch');
    if (ticket.status !== 'open') return refuse('not_open');
    const t = await this.grant(ticket.ticketId, { provider: v.provider, transactionId: v.transactionId, details });
    return t.status === 'granted' ? 'granted' : t.status === 'open' ? 'not_open' : t.status;
  }

  /** Applies the reward of an open ticket (under the profile lock); a ticket that is no longer open is returned as it is. */
  private async grant(ticketId: string, via: { provider: AdProviderId; transactionId: string; details?: JsonObject }): Promise<AdTicketRecord> {
    const first = await this.d.storage.get<AdTicketRecord>(C.adTickets, ticketId);
    if (!first) throw new BackendError('NOT_FOUND', 'No such ad ticket', { reason: 'ad_ticket_not_found', ticketId });
    return this.profiles.withRecord(first.playerId, async (rec, a) => {
      const t = await this.d.storage.get<AdTicketRecord>(C.adTickets, ticketId);
      if (!t || t.status !== 'open') return t ?? first;
      const now = this.d.clock.now();
      const txOp = putOp(C.adTransactions, transactionDocId(via.provider, via.transactionId), { ticketId, at: now });
      let outcome: { state: StoredGameState; serverState: ServerState; result: JsonObject } | null = null;
      let failure: string | null = null;
      if (!rec.profile.gameState) failure = 'no_profile';
      else {
        try {
          outcome = this.reward(t, rec, now);
        } catch (err) {
          if (!isRuleError(err) && !(err instanceof RewardUnavailable)) throw err;
          failure = err.reason;
        }
      }
      const mine = await this.playerRecord(t.playerId);
      const status: AdTicketInfo['status'] = outcome ? 'granted' : 'failed';
      const next: AdTicketRecord = { ...t, status, transactionId: via.transactionId, grantedAt: outcome ? now : null, result: outcome?.result ?? null, failure };
      const player: AdPlayerRecord = { recent: mine.recent.map((r) => (r.ticketId === ticketId ? { ...r, status } : r)) };
      const { entry, op } = this.audit.prepare({
        area: 'ads',
        event: outcome ? 'grant' : 'grant_failed',
        playerId: t.playerId,
        provider: via.provider,
        ok: outcome !== null,
        details: { ...(via.details ?? {}), ticketId, placement: t.placement, ...(outcome ? { result: outcome.result } : { failure }) },
      });
      const ops: StorageBatchOp[] = [putOp(C.adTickets, ticketId, next), putOp(C.adPlayers, t.playerId, player), txOp, op];
      if (outcome) {
        rec.serverState = outcome.serverState;
        delete rec.fresh;
        await this.profiles.commitCanonical(rec, a, outcome.state, now, null, ops, { kind: 'ad', ref: ticketId, op: t.placement });
      } else await this.d.storage.batch(ops);
      this.audit.stored(entry);
      this.open.delete(ticketId);
      return next;
    });
  }

  /** The reward of a ticket applied to the player's state by the game's rules (throws RuleError when it cannot be given). */
  private reward(t: AdTicketRecord, rec: ProfileRecord, now: number): { state: StoredGameState; serverState: ServerState; result: JsonObject } {
    let state = cloneJson(rec.profile.gameState) as unknown as StoredGameState;
    let serverState: ServerState = cloneJson(rec.serverState ?? {});
    const tz = decideTimeZone(rec.tz, undefined, now).offsetMin;
    const ctx = () => serverRuleContext({ nowMs: now, tzOffsetMin: tz, random: this.d.ruleRng, serverState });
    const snap = t.snapshot;
    switch (t.placement) {
      case 'ad_battle_double': {
        const gold = Number(snap.gold) || 0;
        const r = withCurrency(state, gold, 0);
        return { state: r.state, serverState, result: { gold: r.gold } };
      }
      case 'ad_grave_extra': {
        const battle = { mode: String(snap.mode), stage: Number(snap.stage), graves: [{ name: String(snap.name), cpu_level: Number(snap.cpu_level), searched: false }] };
        const ran = runRulesOp('grave.search', state, { index: 1, battle, adBonus: true }, ctx(), SERVER_RULE_OPS);
        const found = ran.result as JsonObject;
        return { state: ran.gameState as StoredGameState, serverState, result: { index: Number(snap.index), ptype: String(found.ptype ?? ''), id: String(found.id ?? '') } };
      }
      case 'ad_darts_free': {
        const currency = snap.currency === 'diam' ? 'diam' : 'gold';
        const amount = Number(snap.amount);
        // A free throw: the stake is added first, so the darts rule takes it back (net: nothing paid).
        state = withCurrency(state, currency === 'gold' ? amount : 0, currency === 'diam' ? amount : 0).state;
        const ran = runRulesOp('roulette.bet', state, { currency, amount }, ctx(), RULE_OPS);
        return { state: ran.gameState as StoredGameState, serverState, result: { ...(ran.result as JsonObject), free: true } };
      }
      case 'ad_daily_gift_x2': {
        const day = Number(snap.day);
        const pending = pendingPresent(serverState);
        let got: JsonObject;
        if (pending && pending.day === day) {
          const ran = runRulesOp('present.claim', state, { available: true }, ctx(), SERVER_RULE_OPS);
          state = ran.gameState as StoredGameState;
          got = ran.result as JsonObject;
          serverState = { ...serverState };
          delete serverState[PRESENT_KEY];
          serverState[LAST_PRESENT_KEY] = { day, ...got };
        } else {
          const last = isPlainObject(serverState[LAST_PRESENT_KEY]) ? (serverState[LAST_PRESENT_KEY] as JsonObject) : null;
          if (!last || last.day !== day) throw new RewardUnavailable('no_present');
          got = last;
        }
        // The present once more (x2 in all).
        const w = decodeState(state);
        const add = (k: 'gold' | 'diam' | 'skill_pts') => Math.max(0, Math.floor(Number(got[k]) || 0));
        w.player.gold = int32(Math.min(2_147_483_647, Number(w.player.gold) + add('gold')));
        w.player.diam = int32(Math.min(2_147_483_647, Number(w.player.diam) + add('diam')));
        w.player.skill_pts = int32(Number(w.player.skill_pts) + add('skill_pts'));
        return { state: encodeState(w), serverState, result: { gold: 2 * add('gold'), diam: 2 * add('diam'), skill_pts: 2 * add('skill_pts'), claimed: pending?.day === day } };
      }
      case 'ad_rest': {
        const w = decodeState(state);
        const before = Number(w.player.tired) || 0;
        w.player.tired = Math.max(0, before - AD_REST_AMOUNT);
        return { state: encodeState(w), serverState, result: { tired: Number(w.player.tired), rested: before - Number(w.player.tired) } };
      }
    }
    throw new RewardUnavailable('unknown_placement');
  }

  // ---------------------------------------------------------------------------------------------- background

  /** The sweep (BackendCore.tick): expires open tickets past their time, prunes old tickets now and then. */
  tick(now: number): Promise<void> {
    if (this.busy) return this.busy;
    this.busy = this.sweep(now)
      .catch((err) => this.d.logger.error('ad ticket sweep failed', { err }))
      .finally(() => {
        this.busy = null;
      });
    return this.busy;
  }

  /** An operator restored a damaged ticket (admin server.repairDocument): an open one is swept like the others. */
  repaired(t: AdTicketRecord): void {
    if (this.openLoaded && t && t.status === 'open' && typeof t.ticketId === 'string') this.open.set(t.ticketId, [t.playerId, t.expiresAt]);
  }

  private async sweep(now: number): Promise<void> {
    if (!this.openLoaded) {
      this.openLoaded = true;
      for (const t of await this.d.storage.list<AdTicketRecord>(C.adTickets)) if (t.status === 'open') this.open.set(t.ticketId, [t.playerId, t.expiresAt]);
    }
    for (const [ticketId, [playerId, expiresAt]] of [...this.open]) {
      if (expiresAt > now) continue;
      await this.profiles.withRecord(playerId, async () => {
        const t = await this.d.storage.get<AdTicketRecord>(C.adTickets, ticketId);
        this.open.delete(ticketId);
        if (!t || t.status !== 'open') return;
        const mine = await this.playerRecord(playerId);
        const player: AdPlayerRecord = { recent: mine.recent.map((r) => (r.ticketId === ticketId ? { ...r, status: 'expired' as const } : r)) };
        const { entry, op } = this.audit.prepare({ area: 'ads', event: 'expired', playerId, provider: t.provider, ok: false, details: { ticketId, placement: t.placement } });
        await this.d.storage.batch([putOp(C.adTickets, ticketId, { ...t, status: 'expired' }), putOp(C.adPlayers, playerId, player), op]);
        this.audit.stored(entry);
      }).catch((err) => {
        // The account is gone (deleted meanwhile): its tickets went with it.
        this.open.delete(ticketId);
        if (!(err instanceof BackendError)) throw err;
      });
    }
    if (now >= this.nextPruneAt) {
      this.nextPruneAt = now + PRUNE_EVERY_MS;
      const keep = this.d.config.ads.keepTicketsMs;
      const old = (await this.d.storage.list<AdTicketRecord>(C.adTickets)).filter((t) => t.status !== 'open' && now - t.createdAt > keep);
      const ops: StorageBatchOp[] = [];
      for (const t of old) {
        ops.push(deleteOp(C.adTickets, t.ticketId));
        if (t.transactionId) ops.push(deleteOp(C.adTransactions, transactionDocId(t.provider, t.transactionId)));
      }
      for (let i = 0; i < ops.length; i += 500) await this.d.storage.batch(ops.slice(i, i + 500));
    }
  }

  // ---------------------------------------------------------------------------------------------- helpers

  /** The player's ad documents that go with the account (tickets, the ad record; transactions of its tickets). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    const ops: StorageBatchOp[] = [];
    for (const t of await this.d.storage.list<AdTicketRecord>(C.adTickets)) {
      if (t.playerId !== playerId) continue;
      ops.push(deleteOp(C.adTickets, t.ticketId));
      if (t.transactionId) ops.push(deleteOp(C.adTransactions, transactionDocId(t.provider, t.transactionId)));
      this.open.delete(t.ticketId);
    }
    if (await this.d.storage.get(C.adPlayers, playerId)) ops.push(deleteOp(C.adPlayers, playerId));
    return ops;
  }

  private async playerRecord(playerId: PlayerId): Promise<AdPlayerRecord> {
    const r = await this.d.storage.get<AdPlayerRecord>(C.adPlayers, playerId);
    return r && Array.isArray(r.recent) ? r : { recent: [] };
  }

  /** The player's calendar day (the game's own daily resets: the session entry's calendar) and when it ends. */
  private dayOf(rec: ProfileRecord | null): { day: number; resetsAt: number } {
    const now = this.d.clock.now();
    const tz = decideTimeZone(rec?.tz, undefined, now).offsetMin;
    const ctx = serverRuleContext({ nowMs: now, tzOffsetMin: tz, random: this.d.ruleRng, serverState: {} });
    const day = ctx.calendar(Math.floor(now / 1000)).dayIndex;
    const local = now + tz * 60_000;
    const resetsAt = (Math.floor(local / 86_400_000) + 1) * 86_400_000 - tz * 60_000;
    return { day, resetsAt };
  }

  private info(t: AdTicketRecord): AdTicketInfo {
    return {
      ticketId: t.ticketId,
      placement: t.placement,
      provider: t.provider,
      status: t.status,
      createdAt: t.createdAt,
      expiresAt: t.expiresAt,
      ssv: { userId: t.ssvUserId, customData: t.ticketId },
      result: (t.result as { [key: string]: JsonValue } | null) ?? null,
      failure: t.failure,
      grantedAt: t.grantedAt,
    };
  }
}

function transactionDocId(provider: string, transactionId: string): string {
  return `tx_${sha256Hex(`${provider}\n${transactionId}`).slice(0, 40)}`;
}

/** A reward that cannot be given any more (the ticket fails with `reason`; never sent as a protocol error). */
class RewardUnavailable extends Error {
  constructor(readonly reason: string) {
    super(reason);
    this.name = 'RewardUnavailable';
  }
}
